import React, { useState, useEffect } from 'react';
import { versionService } from '../../services/versionService';
import { X, RefreshCw, Info, ChevronRight } from 'lucide-react';

export const VersionUpdateNotification: React.FC = () => {
  const [showNotification, setShowNotification] = useState(false);
  const [updateInfo, setUpdateInfo] = useState<{
    oldVersion: string;
    newVersion: string;
    releaseDate: string;
  } | null>(null);
  const [showChangelog, setShowChangelog] = useState(false);
  const [changelog, setChangelog] = useState<any[]>([]);

  useEffect(() => {
    const handleVersionUpdate = (event: CustomEvent) => {
      setUpdateInfo(event.detail);
      setShowNotification(true);
      loadChangelog();
    };

    window.addEventListener('version-update', handleVersionUpdate as EventListener);

    // Verificar se há atualização disponível ao carregar
    if (versionService.hasUpdate()) {
      setShowNotification(true);
      loadChangelog();
    }

    return () => {
      window.removeEventListener('version-update', handleVersionUpdate as EventListener);
    };
  }, []);

  const loadChangelog = async () => {
    const changes = await versionService.getChangelog();
    setChangelog(changes.slice(0, 3)); // Mostrar apenas as 3 últimas versões
  };

  const handleDismiss = () => {
    setShowNotification(false);
    versionService.dismissUpdate();
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  if (!showNotification) return null;

  return (
    <div className="fixed bottom-20 right-4 z-50 max-w-md animate-slide-up">
      <div className="bg-white rounded-lg shadow-xl border border-coral-soft overflow-hidden">
        <div className="bg-coral-deep text-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <RefreshCw className="w-5 h-5" />
              <h3 className="font-semibold">Nova Versão Disponível!</h3>
            </div>
            <button
              onClick={handleDismiss}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-4">
          {updateInfo && (
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Versão <span className="font-semibold">{updateInfo.oldVersion}</span> →{' '}
                <span className="font-semibold text-coral-deep">{updateInfo.newVersion}</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Lançada em {new Date(updateInfo.releaseDate).toLocaleDateString('pt-BR')}
              </p>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={() => setShowChangelog(!showChangelog)}
              className="w-full flex items-center justify-between text-sm text-gray-700 hover:text-coral-deep transition-colors"
            >
              <span className="flex items-center">
                <Info className="w-4 h-4 mr-2" />
                {showChangelog ? 'Ocultar' : 'Ver'} Novidades
              </span>
              <ChevronRight
                className={`w-4 h-4 transition-transform ${
                  showChangelog ? 'rotate-90' : ''
                }`}
              />
            </button>

            {showChangelog && changelog.length > 0 && (
              <div className="max-h-60 overflow-y-auto space-y-3 pt-2 border-t">
                {changelog.map((entry, index) => (
                  <div key={index} className="text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-gray-700">v{entry.version}</span>
                      <span className="text-gray-500">{entry.date}</span>
                    </div>
                    {entry.changes.added && entry.changes.added.length > 0 && (
                      <div className="ml-2">
                        <span className="text-green-600 font-medium">Adicionado:</span>
                        <ul className="list-disc list-inside text-gray-600 mt-1">
                          {entry.changes.added.slice(0, 2).map((item: string, i: number) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {entry.changes.fixed && entry.changes.fixed.length > 0 && (
                      <div className="ml-2 mt-1">
                        <span className="text-coral-deep font-medium">Corrigido:</span>
                        <ul className="list-disc list-inside text-gray-600 mt-1">
                          {entry.changes.fixed.slice(0, 2).map((item: string, i: number) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={handleRefresh}
              className="btn-primary w-full space-x-2"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Atualizar Agora</span>
            </button>

            <button
              onClick={handleDismiss}
              className="w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Lembrar mais tarde
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};